import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, NgClass, NgIf } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService, StudentUser } from '../../services/auth.service';

type CurrentUser = StudentUser | null;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, NgIf, NgClass],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('navbar', { static: true }) navbar?: ElementRef<HTMLElement>;

  private readonly destroyRef = inject(DestroyRef);

  isMenuOpen = false;
  isScrolled = false;
  isHomePage = true;

  user: CurrentUser = null;

  constructor(private readonly router: Router, private readonly authService: AuthService) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.isHomePage = event.urlAfterRedirects === '/' || event.urlAfterRedirects === '';
        this.closeMenu();
        this.updateNavbarStyle();
      });

    // Initial state when the component mounts
    this.isHomePage = this.router.url === '/' || this.router.url === '';
    this.updateNavbarStyle();

    // Subscribe to current user from AuthService
    this.authService
      .currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngAfterViewInit(): void {
    this.updateNavbarStyle();
  }

  ngOnDestroy(): void {
    // Nothing to clean up manually thanks to takeUntilDestroyed
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.isHomePage) {
      return;
    }

    const scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const threshold = window.innerHeight * 0.8;

    const hasScrolledPastThreshold = scrollPosition > threshold;
    if (hasScrolledPastThreshold !== this.isScrolled) {
      this.isScrolled = hasScrolledPastThreshold;
      this.updateNavbarStyle();
    }
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  logout(): void {
    const currentUrl = this.router.url;
    const shouldRedirectToHomepage = currentUrl === '/dashboard' || currentUrl === '/profile';
    
    this.authService.logout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (shouldRedirectToHomepage) {
          this.router.navigate(['/']);
        }
      }
    });
  }

  getCurrentUrl(): string {
    return this.router.url;
  }

  getUserDisplayName(): string {
    if (!this.user) {
      return '';
    }
    return this.user.displayName || this.user.email || 'User';
  }
  
  getUserPhotoUrl(): string | null {
    return this.user?.photoURL || null;
  }

  getUserInitials(): string {
    const displayName = this.getUserDisplayName();
    if (!displayName) {
      return 'U';
    }

    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName.charAt(0).toUpperCase();
  }

  private updateNavbarStyle(): void {
    if (!this.navbar) {
      return;
    }

    const navbarElement = this.navbar.nativeElement;
    const applyScrolledStyles = !this.isHomePage || this.isScrolled;

    navbarElement.classList.toggle('navbar--scrolled', applyScrolledStyles);
  }
}

